import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { createClient } from '@supabase/supabase-js'
import SalaGrabacion from './SalaGrabacion'
import SalaVideos from './SalaVideos'

// Mismo patron que SalaGrabacion.jsx: el navegador sube el archivo
// directamente a Supabase Storage (bucket "fotos-profesores", debe
// crearse como bucket publico) y solo la URL publica resultante se
// guarda en la base de datos.
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

async function subirFotoProfesor(archivo) {
  const ext = (archivo.name?.split('.').pop() || 'jpg').toLowerCase()
  const nombreArchivo = 'profesor_' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.' + ext
  const { error } = await supabase.storage
    .from('fotos-profesores')
    .upload(nombreArchivo, archivo, { contentType: archivo.type })
  if (error) throw error
  const { data } = supabase.storage.from('fotos-profesores').getPublicUrl(nombreArchivo)
  return data.publicUrl
}

const API = process.env.REACT_APP_API_URL

const NIVELES = ['A1','A2','B1','B2','C1','C2']

// Las rutas de administrador ahora exigen esta cabecera en el backend
// (antes cualquiera podia llamarlas sin iniciar sesion — ver
// api/src/middleware/verificarAdmin.js).
function cabeceras(usuario) {
  return { headers: { 'x-usuario-id': usuario?.id } }
}

function formatoFechaHora(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatoCOP(valor) {
  if (valor === null || valor === undefined) return '—'
  return '$' + Number(valor).toLocaleString('es-CO')
}

// Los VideoForos se reunen por un link externo (Meet/Zoom), asi que a
// diferencia de las Clases no hay forma de detectar solo quien entro --
// aqui el profesor/admin marca a mano, despues del foro, quien asistio de
// verdad entre los inscritos.
function ModalAsistenciaForo({ foro, usuario, onCerrar, mostrarMensaje }) {
  const [inscritos, setInscritos] = useState([])
  const [cargando, setCargando] = useState(true)

  function cargar() {
    setCargando(true)
    axios.get(API + '/curriculo/admin/foros/' + foro.id + '/inscritos', cabeceras(usuario))
      .then(r => setInscritos(r.data.datos || []))
      .catch(() => mostrarMensaje('error', 'No se pudo cargar la lista de inscritos.'))
      .finally(() => setCargando(false))
  }

  useEffect(() => { cargar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function alternar(inscripcion) {
    axios.put(API + '/curriculo/admin/foros/inscripciones/' + inscripcion.id + '/asistencia',
      { asistio: !inscripcion.asistio }, cabeceras(usuario))
      .then(cargar)
      .catch(() => mostrarMensaje('error', 'No se pudo actualizar la asistencia.'))
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, maxWidth:440, width:'100%', maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:16, fontWeight:800 }}>Asistencia · {foro.titulo}</h3>
          <button onClick={onCerrar} style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#6B7280' }}>✕</button>
        </div>
        <p style={{ fontSize:12, color:'#6B7280', marginBottom:14 }}>Marca quien de los inscritos asistió de verdad al foro.</p>
        {cargando ? (
          <div style={{ textAlign:'center', padding:20, color:'#6B7280' }}>Cargando...</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {inscritos.map(i => (
              <label key={i.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, background:'#F4F6FA', cursor:'pointer' }}>
                <input type="checkbox" checked={!!i.asistio} onChange={() => alternar(i)} />
                <span style={{ fontSize:13 }}>{i.email || i.usuario_id}</span>
              </label>
            ))}
            {inscritos.length === 0 && (
              <div style={{ textAlign:'center', padding:16, color:'#6B7280', fontSize:13 }}>Nadie se ha inscrito a este foro.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════ EXAMEN DE NIVEL ═══
// Antes, la escritura y los audios de speaking del examen de nivel SOLO se
// autocalificaban solos (Speaking ni siquiera grababa audio real) y no
// quedaba ningun registro para que un profesor confirmara el nivel real
// del estudiante. Ahora ExamenNivel.jsx graba la voz de verdad y, al
// registrarse, Login.jsx envia esa escritura + esos audios aqui — esta
// pestaña trae la cola de revision, igual que ya existe en EnglishBridge.
const estilosExamen = {
  tab: activo => ({
    padding: '9px 18px', borderRadius: 10, border: `2px solid ${activo ? '#1B3A8C' : '#E2E6F0'}`,
    background: activo ? '#1B3A8C' : '#fff', color: activo ? '#fff' : '#374151',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer'
  }),
  card: { background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: 18, marginBottom: 14 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' },
  input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #E2E6F0', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' },
  boton: { padding: '9px 18px', borderRadius: 10, border: 'none', background: '#1B3A8C', color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }
}

function FilaExamenNivel({ examen, usuario, mostrarMensaje, onCalificado }) {
  const [nivelFinal, setNivelFinal] = useState(examen.nivel_final || '')
  const [notas, setNotas] = useState(examen.notas_admin || '')
  const [guardando, setGuardando] = useState(false)
  const soloLectura = examen.estado === 'revisado'

  async function calificar() {
    if (!nivelFinal) { mostrarMensaje('error', 'Elige un nivel final antes de guardar.'); return }
    setGuardando(true)
    try {
      await axios.post(`${API}/examen-revision/admin/${examen.id}/calificar`,
        { nivel_final: nivelFinal, notas_admin: notas }, cabeceras(usuario))
      mostrarMensaje('ok', 'Examen calificado.')
      onCalificado()
    } catch (err) {
      mostrarMensaje('error', err.response?.data?.error || 'No se pudo guardar la calificación.')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div style={estilosExamen.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{examen.email}</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            Nivel estimado (auto): {examen.nivel_estimado || 'N/A'} · Recibido: {formatoFechaHora(examen.creado_en)}
          </div>
        </div>
        {soloLectura && (
          <div style={{ fontSize: 13, fontWeight: 800, color: '#166534' }}>
            ✅ Nivel final: {examen.nivel_final}
          </div>
        )}
      </div>

      {(examen.escritura || []).map((e, i) => (
        <div key={i} style={{ background: '#F4F6FA', borderRadius: 8, padding: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1B3A8C', marginBottom: 4 }}>✍️ Writing · {e.nivel}</div>
          <div style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic', marginBottom: 6 }}>{e.pregunta}</div>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{e.texto}</div>
        </div>
      ))}

      {(examen.audios || []).map((a, i) => (
        <div key={i} style={{ background: '#F4F6FA', borderRadius: 8, padding: 12, marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1B3A8C', marginBottom: 6 }}>🎤 Speaking</div>
          {a.url
            ? <audio controls src={a.url} style={{ width: '100%' }} />
            : <div style={{ fontSize: 12, color: '#9CA3AF' }}>Audio no disponible</div>}
        </div>
      ))}

      {!soloLectura && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginTop: 10, paddingTop: 10, borderTop: '1px solid #E2E6F0', flexWrap: 'wrap' }}>
          <div>
            <label style={estilosExamen.label}>Nivel final</label>
            <select value={nivelFinal} onChange={e => setNivelFinal(e.target.value)} style={{ ...estilosExamen.input, width: 140 }}>
              <option value="">Elegir...</option>
              {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={estilosExamen.label}>Notas (opcional)</label>
            <input type="text" value={notas} onChange={e => setNotas(e.target.value)} style={estilosExamen.input} placeholder="Observaciones para el estudiante o el equipo" />
          </div>
          <button onClick={calificar} disabled={guardando} style={estilosExamen.boton}>
            {guardando ? 'Guardando...' : 'Guardar calificación'}
          </button>
        </div>
      )}
      {soloLectura && examen.notas_admin && (
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8, paddingTop: 8, borderTop: '1px solid #E2E6F0' }}>
          Notas: {examen.notas_admin}
        </div>
      )}
    </div>
  )
}

function TabExamenNivel({ usuario, mostrarMensaje }) {
  const [subTab, setSubTab] = useState('pendiente')
  const [examenes, setExamenes] = useState([])
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(() => {
    setCargando(true)
    axios.get(`${API}/examen-revision/admin/listado`, { ...cabeceras(usuario), params: { estado: subTab } })
      .then(r => setExamenes(r.data.datos || []))
      .catch(() => mostrarMensaje('error', 'No se pudieron cargar los exámenes.'))
      .finally(() => setCargando(false))
  }, [usuario, subTab]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <button style={estilosExamen.tab(subTab === 'pendiente')} onClick={() => setSubTab('pendiente')}>⏳ Pendientes</button>
        <button style={estilosExamen.tab(subTab === 'revisado')} onClick={() => setSubTab('revisado')}>✅ Revisados</button>
      </div>
      {cargando && <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>Cargando...</div>}
      {!cargando && examenes.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: '#6B7280' }}>
          {subTab === 'pendiente' ? 'No hay exámenes pendientes por calificar.' : 'Todavía no hay exámenes revisados.'}
        </div>
      )}
      {!cargando && examenes.map(ex => (
        <FilaExamenNivel key={ex.id} examen={ex} usuario={usuario} mostrarMensaje={mostrarMensaje} onCalificado={cargar} />
      ))}
    </div>
  )
}

function StatCard({ ic, valor, label, color }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:'20px 16px', textAlign:'center' }}>
      <div style={{ fontSize:28, marginBottom:6 }}>{ic}</div>
      <div style={{ fontSize:32, fontWeight:900, color: color || '#1A1A2E' }}>{valor}</div>
      <div style={{ fontSize:12, color:'#6B7280', fontWeight:600, marginTop:2 }}>{label}</div>
    </div>
  )
}

export default function PanelAdmin({ usuario, onLogout, standalone }) {
  const [seccion,     setSeccion]     = useState('estadisticas')
  const [stats,       setStats]       = useState(null)
  const [clases,      setClases]      = useState([])
  const [foros,       setForos]       = useState([])
  const [estudiantes, setEstudiantes] = useState([])
  const [profesores,  setProfesores]  = useState([])
  const [lecturas,    setLecturas]    = useState([])
  const [registrosPlan, setRegistrosPlan] = useState([])
  const [reservasClases, setReservasClases] = useState([])
  const [pagos,       setPagos]       = useState([])
  const [horasPorProfesor, setHorasPorProfesor] = useState({})
  const [verAsistenciaForo, setVerAsistenciaForo] = useState(null) // foro seleccionado, o null
  const [cargando,    setCargando]    = useState(true)
  const [modal,       setModal]       = useState(null)
  const [form,        setForm]        = useState({})
  const [guardando,   setGuardando]   = useState(false)
  const [mensaje,     setMensaje]     = useState(null)
  const [subiendoFoto, setSubiendoFoto] = useState(false)

  async function elegirFotoProfesor(e) {
    const archivo = e.target.files?.[0]
    if (!archivo) return
    setSubiendoFoto(true)
    try {
      const url = await subirFotoProfesor(archivo)
      setForm(f => ({ ...f, foto_url: url }))
    } catch (err) {
      mostrarMensaje('error', 'No se pudo subir la foto. Intenta de nuevo.')
    }
    setSubiendoFoto(false)
    e.target.value = ''
  }

  useEffect(() => { cargarDatos() }, [seccion])

  async function cargarDatos() {
    setCargando(true)
    try {
      if (seccion === 'estadisticas') {
        const [statsRes, clasesRes, forosRes] = await Promise.all([
          axios.get(API + '/curriculo/admin/estadisticas', cabeceras(usuario)),
          axios.get(API + '/curriculo/clases'),
          axios.get(API + '/curriculo/videoforos')
        ])
        setStats(statsRes.data.datos)
        setClases(clasesRes.data.datos || [])
        setForos(forosRes.data.datos || [])
      }
      if (seccion === 'clases') {
        const [clasesRes, profRes] = await Promise.all([
          axios.get(API + '/curriculo/clases'),
          axios.get(API + '/curriculo/admin/profesores', cabeceras(usuario))
        ])
        setClases(clasesRes.data.datos || [])
        setProfesores(profRes.data.datos || [])
      }
      if (seccion === 'foros') {
        const [forosRes, profRes] = await Promise.all([
          axios.get(API + '/curriculo/videoforos'),
          axios.get(API + '/curriculo/admin/profesores', cabeceras(usuario))
        ])
        setForos(forosRes.data.datos || [])
        setProfesores(profRes.data.datos || [])
      }
      if (seccion === 'estudiantes') {
        const [estRes, planRes] = await Promise.all([
          axios.get(API + '/curriculo/admin/estudiantes', cabeceras(usuario)),
          axios.get(API + '/notificaciones/registros-plan')
        ])
        setEstudiantes(estRes.data.datos || [])
        setRegistrosPlan(planRes.data.datos || [])
      }
      if (seccion === 'profesores') {
        const [profRes, horasRes] = await Promise.all([
          axios.get(API + '/curriculo/admin/profesores', cabeceras(usuario)),
          axios.get(API + '/curriculo/admin/horas-profesor', cabeceras(usuario)).catch(() => ({ data: { datos: [] } }))
        ])
        setProfesores(profRes.data.datos || [])
        setHorasPorProfesor(Object.fromEntries((horasRes.data.datos || []).map(h => [h.profesor_id, h])))
      }
      if (seccion === 'reservas') {
        const res = await axios.get(API + '/curriculo/admin/reservas-clases', cabeceras(usuario))
        setReservasClases(res.data.datos || [])
      }
      if (seccion === 'pagos') {
        const res = await axios.get(API + '/pagos/admin/pagos', cabeceras(usuario))
        setPagos(res.data.datos || [])
      }
      if (seccion === 'lecturas') {
        const res = await axios.get(API + '/contenido/listar')
        setLecturas(res.data.datos || [])
      }
    } catch (err) { console.error(err) }
    finally { setCargando(false) }
  }

  function mostrarMensaje(tipo, texto) {
    setMensaje({ tipo, texto })
    setTimeout(() => setMensaje(null), 4000)
  }

  async function guardarClase() {
    setGuardando(true)
    try {
      if (form.id) {
        await axios.put(API + '/curriculo/admin/clases/' + form.id, form, cabeceras(usuario))
        mostrarMensaje('ok', 'Clase actualizada correctamente')
      } else {
        await axios.post(API + '/curriculo/admin/clases', form, cabeceras(usuario))
        mostrarMensaje('ok', 'Clase creada correctamente')
      }
      setModal(null); setForm({}); cargarDatos()
    } catch (err) {
      mostrarMensaje('error', err.response?.data?.error || 'Error al guardar')
    } finally { setGuardando(false) }
  }

  async function eliminarClase(id) {
    if (!window.confirm('Eliminar esta clase?')) return
    try {
      await axios.delete(API + '/curriculo/admin/clases/' + id, cabeceras(usuario))
      mostrarMensaje('ok', 'Clase eliminada')
      cargarDatos()
    } catch (err) { mostrarMensaje('error', 'Error al eliminar') }
  }

  async function guardarForo() {
    setGuardando(true)
    try {
      if (form.id) {
        await axios.put(API + '/curriculo/admin/foros/' + form.id, form, cabeceras(usuario))
        mostrarMensaje('ok', 'Foro actualizado correctamente')
      } else {
        await axios.post(API + '/curriculo/admin/foros', form, cabeceras(usuario))
        mostrarMensaje('ok', 'Foro creado correctamente')
      }
      setModal(null); setForm({}); cargarDatos()
    } catch (err) {
      mostrarMensaje('error', err.response?.data?.error || 'Error al guardar')
    } finally { setGuardando(false) }
  }

  async function eliminarForo(id) {
    if (!window.confirm('Eliminar este foro?')) return
    try {
      await axios.delete(API + '/curriculo/admin/foros/' + id, cabeceras(usuario))
      mostrarMensaje('ok', 'Foro eliminado')
      cargarDatos()
    } catch (err) { mostrarMensaje('error', 'Error al eliminar') }
  }

  async function guardarProfesor() {
    setGuardando(true)
    try {
      if (form.id) {
        await axios.put(API + '/curriculo/admin/profesores/' + form.id, form, cabeceras(usuario))
        mostrarMensaje('ok', 'Profesor actualizado correctamente')
      } else {
        await axios.post(API + '/curriculo/admin/profesores', form, cabeceras(usuario))
        mostrarMensaje('ok', 'Profesor creado correctamente')
      }
      setModal(null); setForm({}); cargarDatos()
    } catch (err) {
      mostrarMensaje('error', err.response?.data?.error || 'Error al guardar')
    } finally { setGuardando(false) }
  }

  async function cambiarActivoProfesor(p) {
    try {
      await axios.put(API + '/curriculo/admin/profesores/' + p.id, { activo: !p.activo }, cabeceras(usuario))
      mostrarMensaje('ok', p.activo ? 'Profesor desactivado' : 'Profesor activado')
      cargarDatos()
    } catch (err) {
      mostrarMensaje('error', 'No se pudo actualizar el profesor')
    }
  }

  async function guardarLectura() {
    setGuardando(true)
    try {
      if (form.id) {
        await axios.put(API + '/contenido/' + form.id, form, cabeceras(usuario))
        mostrarMensaje('ok', 'Lectura actualizada correctamente')
      } else {
        await axios.post(API + '/contenido/crear', { ...form, creado_por: usuario?.id }, cabeceras(usuario))
        mostrarMensaje('ok', 'Lectura creada correctamente')
      }
      setModal(null); setForm({}); cargarDatos()
    } catch (err) {
      mostrarMensaje('error', err.response?.data?.error || 'Error al guardar')
    } finally { setGuardando(false) }
  }

  async function eliminarLectura(id) {
    if (!window.confirm('Eliminar esta lectura?')) return
    try {
      await axios.delete(API + '/contenido/' + id, cabeceras(usuario))
      mostrarMensaje('ok', 'Lectura eliminada')
      cargarDatos()
    } catch (err) { mostrarMensaje('error', 'Error al eliminar') }
  }

  async function editarLectura(id) {
    try {
      const res = await axios.get(API + '/contenido/' + id)
      setForm(res.data.datos)
      setModal('lectura')
    } catch (err) { mostrarMensaje('error', 'Error al cargar la lectura') }
  }

  const inputStyle = {
    width:'100%', padding:'9px 12px', border:'1.5px solid #E2E6F0',
    borderRadius:8, fontFamily:'inherit', fontSize:14, outline:'none',
    boxSizing:'border-box', marginBottom:12
  }
  const labelStyle = { fontSize:12, fontWeight:700, color:'#374151', marginBottom:4, display:'block' }

  const NAV_ADMIN = [
    { id:'estadisticas', ic:'📊', label:'Estadisticas'   },
    { id:'clases',       ic:'🎥', label:'Clases en vivo'  },
    { id:'reservas',     ic:'📅', label:'Reservas'        },
    { id:'foros',        ic:'🎙', label:'VideoForos'      },
    { id:'lecturas',     ic:'📖', label:'Lecturas'        },
    { id:'grabacion',    ic:'🎤', label:'Sala de grabacion' },
    { id:'videos',       ic:'🎬', label:'Videos de lecciones' },
    { id:'pagos',        ic:'💳', label:'Pagos'           },
    { id:'estudiantes',  ic:'👥', label:'Estudiantes'     },
    { id:'profesores',   ic:'👨‍🏫', label:'Profesores'     },
    { id:'examen_nivel', ic:'📝', label:'Examen de nivel' },
  ]

  return (
    <div style={{ minHeight:'100vh', background:'#F4F6FA', fontFamily:'system-ui, sans-serif' }}>

      {/* Header standalone */}
      {standalone && (
        <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', padding:'16px 32px', color:'#fff', display:'flex', alignItems:'center', gap:16 }}>
          <button onClick={() => setSeccion('estadisticas')} title="EspañolGo — inicio"
            style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:8 }}>
            <img src="/edunova-logo.png" alt="EduNova" style={{ width:24, height:24, objectFit:'contain' }} />
            <div style={{ fontSize:20, fontWeight:900 }}>
              <span style={{ color:'#FFD700' }}>Esp</span>
              <span style={{ color:'#7BA7E8' }}>añ</span>
              <span style={{ color:'#FF8A8A' }}>olGo</span>
            </div>
          </button>
          <div style={{ width:1, height:24, background:'rgba(255,255,255,0.3)' }} />
          <div style={{ fontSize:14, fontWeight:600 }}>Panel Administrador</div>
          <div style={{ fontSize:10, opacity:.6 }}>by EduNova Corporation</div>
          <div style={{ flex:1 }} />
          <div style={{ fontSize:13, opacity:.8 }}>{usuario?.email}</div>
          <button onClick={onLogout}
            style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:8, padding:'6px 14px', color:'#fff', fontFamily:'inherit', fontSize:12, cursor:'pointer', marginLeft:8 }}>
            Cerrar sesion
          </button>
        </div>
      )}

      <div style={{ maxWidth:1000, margin:'0 auto', padding:28 }}>

        {/* Header interno */}
        <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:16, padding:24, marginBottom:24, color:'#fff' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:32 }}>⚙️</span>
            <div>
              <h1 style={{ fontSize:22, fontWeight:800, marginBottom:2 }}>Panel de Administrador</h1>
              <p style={{ fontSize:13, opacity:.8 }}>Gestiona clases, foros, lecturas, estudiantes y profesores de EspañolGo.</p>
            </div>
          </div>
        </div>

        {mensaje && (
          <div style={{ padding:'12px 16px', borderRadius:10, marginBottom:16, fontSize:14, fontWeight:600,
            background: mensaje.tipo==='ok' ? '#F0FDF4' : '#FEF2F2',
            color: mensaje.tipo==='ok' ? '#166534' : '#991B1B',
            border:'1px solid ' + (mensaje.tipo==='ok' ? '#22C55E' : '#EF4444') }}>
            {mensaje.tipo==='ok' ? '✅' : '❌'} {mensaje.texto}
          </div>
        )}

        {/* Nav */}
        <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
          {NAV_ADMIN.map(item => (
            <button key={item.id} onClick={() => setSeccion(item.id)}
              style={{ padding:'8px 18px', borderRadius:10, border:'2px solid ' + (seccion===item.id ? '#1B3A8C' : '#E2E6F0'),
                background: seccion===item.id ? '#1B3A8C' : '#fff',
                color: seccion===item.id ? '#fff' : '#374151',
                fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer',
                display:'flex', alignItems:'center', gap:6 }}>
              <span>{item.ic}</span><span>{item.label}</span>
            </button>
          ))}
        </div>

        {seccion === 'grabacion' && <SalaGrabacion usuario={usuario} />}
        {seccion === 'videos' && <SalaVideos usuario={usuario} />}
        {seccion === 'examen_nivel' && <TabExamenNivel usuario={usuario} mostrarMensaje={mostrarMensaje} />}

        {seccion !== 'grabacion' && seccion !== 'videos' && seccion !== 'examen_nivel' && (cargando ? (
          <div style={{ textAlign:'center', padding:60, color:'#6B7280' }}>Cargando...</div>
        ) : (
          <>
            {/* ESTADISTICAS */}
            {seccion === 'estadisticas' && stats && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:28 }}>
                  <StatCard ic='👥' valor={stats.total_usuarios}   label='Estudiantes registrados' color='#1B3A8C' />
                  <StatCard ic='🎥' valor={stats.total_clases}     label='Clases programadas'      color='#7C3AED' />
                  <StatCard ic='🎙' valor={stats.total_foros}      label='VideoForos programados'  color='#059669' />
                  <StatCard ic='🔥' valor={stats.total_retos}      label='Retos activos'           color='#EF4444' />
                  <StatCard ic='📋' valor={stats.total_inscClases} label='Inscripciones en clases' color='#F59E0B' />
                  <StatCard ic='🎤' valor={stats.total_inscForos}  label='Inscripciones en foros'  color='#3B82F6' />
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20 }}>
                    <h3 style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>🎥 Proximas clases</h3>
                    {clases.slice(0,5).map(c => (
                      <div key={c.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #F4F6FA' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{c.nombre}</div>
                          <div style={{ fontSize:11, color:'#6B7280' }}>{c.fecha} · {c.hora_inicio}</div>
                        </div>
                        <div style={{ fontSize:12, color:'#22C55E', fontWeight:700 }}>{c.cupos_disponibles} cupos</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20 }}>
                    <h3 style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>🎙 Proximos foros</h3>
                    {foros.slice(0,5).map(f => (
                      <div key={f.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #F4F6FA' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{f.titulo}</div>
                          <div style={{ fontSize:11, color:'#6B7280' }}>{f.fecha} · {f.hora_inicio}</div>
                        </div>
                        <div style={{ fontSize:12, color:'#22C55E', fontWeight:700 }}>{f.cupos_disponibles} cupos</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* CLASES */}
            {seccion === 'clases' && (
              <div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                  <button onClick={() => { setForm({ cupos_total:8, duracion_min:60, tipo:'grupal', activo:true }); setModal('clase') }}
                    style={{ background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    + Nueva clase
                  </button>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {clases.map(c => (
                    <div key={c.id} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:12, padding:'16px 20px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ background:'#1B3A8C', color:'#fff', fontSize:11, fontWeight:800, padding:'2px 8px', borderRadius:999 }}>{c.nivel_id}</span>
                            <span style={{ fontSize:13, fontWeight:700 }}>{c.nombre}</span>
                          </div>
                          <div style={{ fontSize:12, color:'#6B7280', marginBottom:4 }}>
                            📅 {c.fecha} · ⏰ {c.hora_inicio} - {c.hora_fin} · 👤 {c.profesor_nombre}
                          </div>
                          {c.tema && <div style={{ fontSize:12, color:'#7C3AED' }}>Tema: {c.tema}</div>}
                        </div>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <div style={{ textAlign:'center' }}>
                            <div style={{ fontSize:18, fontWeight:800, color:'#22C55E' }}>{c.cupos_disponibles}</div>
                            <div style={{ fontSize:10, color:'#6B7280' }}>cupos</div>
                          </div>
                          <button onClick={() => { setForm({...c}); setModal('clase') }}
                            style={{ background:'#EDE9FE', color:'#7C3AED', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            Editar
                          </button>
                          <button onClick={() => eliminarClase(c.id)}
                            style={{ background:'#FEF2F2', color:'#EF4444', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FOROS */}
            {seccion === 'foros' && (
              <div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                  <button onClick={() => { setForm({ cupos_total:10, duracion_min:75, activo:true }); setModal('foro') }}
                    style={{ background:'#7C3AED', color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    + Nuevo foro
                  </button>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {foros.map(f => (
                    <div key={f.id} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:12, padding:'16px 20px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ background:'#7C3AED', color:'#fff', fontSize:11, fontWeight:800, padding:'2px 8px', borderRadius:999 }}>{f.nivel_id}</span>
                            <span style={{ fontSize:13, fontWeight:700 }}>{f.titulo}</span>
                          </div>
                          <div style={{ fontSize:12, color:'#6B7280', marginBottom:4 }}>
                            📅 {f.fecha} · ⏰ {f.hora_inicio} - {f.hora_fin} · 👤 {f.profesor_nombre}
                          </div>
                          {f.tema && <div style={{ fontSize:12, color:'#7C3AED' }}>Tema: {f.tema}</div>}
                        </div>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <div style={{ textAlign:'center' }}>
                            <div style={{ fontSize:18, fontWeight:800, color:'#22C55E' }}>{f.cupos_disponibles}</div>
                            <div style={{ fontSize:10, color:'#6B7280' }}>cupos</div>
                          </div>
                          <button onClick={() => setVerAsistenciaForo(f)}
                            style={{ background:'#DCFCE7', color:'#166534', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            ✓ Asistencia
                          </button>
                          <button onClick={() => { setForm({...f}); setModal('foro') }}
                            style={{ background:'#EDE9FE', color:'#7C3AED', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            Editar
                          </button>
                          <button onClick={() => eliminarForo(f.id)}
                            style={{ background:'#FEF2F2', color:'#EF4444', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LECTURAS */}
            {seccion === 'lecturas' && (
              <div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                  <button onClick={() => { setForm({ tipo:'cuento', nivel:'A1', tiempo_lectura_min:3 }); setModal('lectura') }}
                    style={{ background:'#F59E0B', color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    + Nueva lectura
                  </button>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {lecturas.map(l => (
                    <div key={l.id} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:12, padding:'16px 20px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ background:'#F59E0B', color:'#fff', fontSize:11, fontWeight:800, padding:'2px 8px', borderRadius:999 }}>{l.nivel}</span>
                            <span style={{ fontSize:11, color:'#6B7280', fontWeight:600 }}>{l.tipo === 'cuento' ? '📖 Cuento' : '📰 Noticia'}</span>
                            <span style={{ fontSize:13, fontWeight:700 }}>{l.titulo}</span>
                          </div>
                          <div style={{ fontSize:12, color:'#6B7280' }}>
                            ⏱ {l.tiempo_lectura_min} min de lectura · {l.creado_en ? new Date(l.creado_en).toLocaleDateString('es-CO') : ''}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <button onClick={() => editarLectura(l.id)}
                            style={{ background:'#FEF3C7', color:'#92400E', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            Editar
                          </button>
                          <button onClick={() => eliminarLectura(l.id)}
                            style={{ background:'#FEF2F2', color:'#EF4444', border:'none', borderRadius:8, padding:'6px 12px', fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {lecturas.length === 0 && (
                    <div style={{ textAlign:'center', padding:40, color:'#6B7280' }}>No hay lecturas creadas todavia.</div>
                  )}
                </div>
              </div>
            )}

            {/* ESTUDIANTES */}
            {seccion === 'estudiantes' && (
              <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, overflow:'hidden' }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid #E2E6F0' }}>
                  <h3 style={{ fontSize:15, fontWeight:700 }}>👥 Estudiantes registrados ({estudiantes.length})</h3>
                </div>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#F4F6FA' }}>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Email</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Plan elegido</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Fecha registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estudiantes.map((e, i) => {
                      const plan = registrosPlan.find(p => p.email === e.email)
                      return (
                        <tr key={e.id} style={{ borderBottom:'1px solid #F4F6FA', background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                          <td style={{ padding:'10px 16px', fontSize:13 }}>{e.email}</td>
                          <td style={{ padding:'10px 16px', fontSize:13 }}>
                            {plan
                              ? <span style={{ background:'#EEF2FF', color:'#1B3A8C', borderRadius:999, padding:'3px 10px', fontSize:12, fontWeight:700 }}>{plan.plan_nombre}</span>
                              : <span style={{ color:'#9CA3AF', fontStyle:'italic' }}>Sin registrar</span>
                            }
                          </td>
                          <td style={{ padding:'10px 16px', fontSize:12, color:'#6B7280' }}>
                            {e.creado_en ? new Date(e.creado_en).toLocaleDateString('es-CO') : '-'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* RESERVAS */}
            {seccion === 'reservas' && (
              <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, overflow:'hidden' }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid #E2E6F0' }}>
                  <h3 style={{ fontSize:15, fontWeight:700 }}>📅 Reservas a clases ({reservasClases.length})</h3>
                </div>
                <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#F4F6FA' }}>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Estudiante</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Clase</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Profesor</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Fecha / Hora</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Asistió</th>
                      <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Inscrito el</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservasClases.map((r, i) => (
                      <tr key={r.id} style={{ borderBottom:'1px solid #F4F6FA', background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ padding:'10px 16px', fontSize:13 }}>{r.usuario_email || r.usuario_id}</td>
                        <td style={{ padding:'10px 16px', fontSize:13 }}>{r.clase_nombre || '—'}</td>
                        <td style={{ padding:'10px 16px', fontSize:13 }}>{r.profesor_nombre || '—'}</td>
                        <td style={{ padding:'10px 16px', fontSize:12, color:'#6B7280' }}>{r.clase_fecha || '—'} {r.clase_hora_inicio ? '· ' + r.clase_hora_inicio.slice(0,5) : ''}</td>
                        <td style={{ padding:'10px 16px', fontSize:13 }}>
                          {/* Se marca sola cuando el estudiante entra de verdad
                              a la sala de video (videollamada.js /token) */}
                          {r.asistio
                            ? <span style={{ color:'#166534', fontWeight:700 }}>✓ Sí</span>
                            : <span style={{ color:'#9CA3AF' }}>— No</span>}
                        </td>
                        <td style={{ padding:'10px 16px', fontSize:12, color:'#6B7280' }}>{formatoFechaHora(r.creado_en)}</td>
                      </tr>
                    ))}
                    {reservasClases.length === 0 && (
                      <tr><td colSpan={6} style={{ padding:20, textAlign:'center', color:'#6B7280', fontSize:13 }}>Todavía no hay reservas.</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {/* PAGOS */}
            {seccion === 'pagos' && (
              <div>
                <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
                  <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:'20px 16px', textAlign:'center', flex:'1 1 200px' }}>
                    <div style={{ fontSize:12, color:'#6B7280', fontWeight:700, marginBottom:6 }}>SUSCRIPCIONES ACTIVAS</div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#166534' }}>{pagos.filter(p => p.estado === 'activa' || p.estado === 'ACTIVE').length}</div>
                  </div>
                  <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:'20px 16px', textAlign:'center', flex:'1 1 200px' }}>
                    <div style={{ fontSize:12, color:'#6B7280', fontWeight:700, marginBottom:6 }}>TOTAL DE REGISTROS</div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#374151' }}>{pagos.length}</div>
                  </div>
                </div>
                <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, overflow:'hidden' }}>
                  <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:'#F4F6FA' }}>
                        <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Fecha</th>
                        <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Estudiante</th>
                        <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Plan</th>
                        <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Precio</th>
                        <th style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6B7280' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagos.map((p, i) => (
                        <tr key={p.id} style={{ borderBottom:'1px solid #F4F6FA', background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                          <td style={{ padding:'10px 16px', fontSize:12, color:'#6B7280' }}>{formatoFechaHora(p.fecha_inicio)}</td>
                          <td style={{ padding:'10px 16px', fontSize:13 }}>{p.usuario_email}</td>
                          <td style={{ padding:'10px 16px', fontSize:13 }}>{p.plan_nombre}</td>
                          <td style={{ padding:'10px 16px', fontSize:13 }}>{formatoCOP(p.precio)}</td>
                          <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, color: (p.estado === 'activa' || p.estado === 'ACTIVE') ? '#166534' : (p.estado === 'cancelada' || p.estado === 'CANCELLED') ? '#991B1B' : '#8A6D1D' }}>
                            {p.estado}
                          </td>
                        </tr>
                      ))}
                      {pagos.length === 0 && (
                        <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:'#6B7280', fontSize:13 }}>Todavía no hay pagos registrados.</td></tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}

            {/* PROFESORES */}
            {seccion === 'profesores' && (
              <div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
                  <button onClick={() => { setForm({}); setModal('profesor') }}
                    style={{ background:'#059669', color:'#fff', border:'none', borderRadius:10, padding:'10px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                    + Nuevo profesor
                  </button>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px,1fr))', gap:14 }}>
                  {profesores.map(p => {
                    const horas = horasPorProfesor[p.id]
                    const activo = p.activo !== false
                    return (
                    <div key={p.id} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:20, opacity: activo ? 1 : 0.6 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ width:56, height:56, borderRadius:'50%', background:'#E8EFFE', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, marginBottom:12 }}>
                          {p.foto_url ? <img src={p.foto_url} alt={p.nombre} style={{ width:56, height:56, borderRadius:'50%', objectFit:'cover' }} /> : '👤'}
                        </div>
                        {!activo && (
                          <span style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', background:'#F4F6FA', borderRadius:8, padding:'3px 8px' }}>Inactivo</span>
                        )}
                      </div>
                      <div style={{ fontSize:16, fontWeight:700, marginBottom:4 }}>{p.nombre} {p.apellido}</div>
                      <div style={{ fontSize:12, color:'#6B7280', marginBottom:8 }}>{p.email}</div>
                      <div style={{ fontSize:12, fontWeight:700, color: horas ? '#1B3A8C' : '#9CA3AF', borderTop:'1px solid #F4F6FA', paddingTop:8, marginBottom:12 }}>
                        {horas
                          ? <>⏱ {horas.horas_totales}h dictadas <span style={{ color:'#6B7280', fontWeight:400 }}>({horas.clases_dictadas} clases)</span></>
                          : '⏱ Sin clases dictadas todavía'}
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => { setForm(p); setModal('profesor') }}
                          style={{ flex:1, padding:'7px 0', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:8, fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                          ✏️ Editar
                        </button>
                        <button onClick={() => cambiarActivoProfesor(p)}
                          style={{ flex:1, padding:'7px 0', background: activo ? '#FEF2F2' : '#ECFDF5', color: activo ? '#B91C1C' : '#047857', border:'none', borderRadius:8, fontFamily:'inherit', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                          {activo ? '🚫 Desactivar' : '✅ Activar'}
                        </button>
                      </div>
                    </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        ))}
      </div>

      {/* Modal clase */}
      {modal === 'clase' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:560, width:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h2 style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>{form.id ? 'Editar clase' : 'Nueva clase en vivo'}</h2>
            <label style={labelStyle}>Nombre de la clase</label>
            <input style={inputStyle} value={form.nombre||''} onChange={e => setForm(f => ({...f, nombre:e.target.value}))} placeholder="Ej: Conversacion A1 - Saludos" />
            <label style={labelStyle}>Descripcion</label>
            <textarea style={{...inputStyle, minHeight:70, resize:'vertical'}} value={form.descripcion||''} onChange={e => setForm(f => ({...f, descripcion:e.target.value}))} placeholder="Descripcion de la clase..." />
            <label style={labelStyle}>Tema principal</label>
            <input style={inputStyle} value={form.tema||''} onChange={e => setForm(f => ({...f, tema:e.target.value}))} placeholder="Ej: Saludos y presentaciones" />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={labelStyle}>Nivel</label>
                <select style={inputStyle} value={form.nivel_id||''} onChange={e => setForm(f => ({...f, nivel_id:e.target.value}))}>
                  <option value=''>Seleccionar nivel</option>
                  {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Profesor</label>
                <select style={inputStyle} value={form.profesor_id||''} onChange={e => setForm(f => ({...f, profesor_id:e.target.value}))}>
                  <option value=''>Seleccionar profesor</option>
                  {profesores.map(p => <option key={p.id} value={p.id}>{p.nombre} {p.apellido}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Fecha</label>
                <input type='date' style={inputStyle} value={form.fecha||''} onChange={e => setForm(f => ({...f, fecha:e.target.value}))} />
              </div>
              <div>
                <label style={labelStyle}>Cupos (max. 8)</label>
                <input type='number' min={1} max={8} style={inputStyle} value={form.cupos_total||8} onChange={e => setForm(f => ({...f, cupos_total:parseInt(e.target.value)}))} />
              </div>
              <div>
                <label style={labelStyle}>Hora inicio</label>
                <input type='time' style={inputStyle} value={form.hora_inicio||''} onChange={e => setForm(f => ({...f, hora_inicio:e.target.value}))} />
              </div>
              <div>
                <label style={labelStyle}>Hora fin</label>
                <input type='time' style={inputStyle} value={form.hora_fin||''} onChange={e => setForm(f => ({...f, hora_fin:e.target.value}))} />
              </div>
            </div>
            <label style={labelStyle}>Link de la clase</label>
            <input style={inputStyle} value={form.link_clase||''} onChange={e => setForm(f => ({...f, link_clase:e.target.value}))} placeholder="https://meet.google.com/..." />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <button onClick={() => { setModal(null); setForm({}) }}
                style={{ padding:'10px 20px', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardarClase} disabled={guardando}
                style={{ padding:'10px 24px', background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                {guardando ? 'Guardando...' : form.id ? 'Actualizar' : 'Crear clase'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal foro */}
      {modal === 'foro' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:560, width:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h2 style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>{form.id ? 'Editar VideoForo' : 'Nuevo VideoForo'}</h2>
            <label style={labelStyle}>Titulo del foro</label>
            <input style={inputStyle} value={form.titulo||''} onChange={e => setForm(f => ({...f, titulo:e.target.value}))} placeholder="Ej: Charla sobre energia eolica" />
            <label style={labelStyle}>Descripcion</label>
            <textarea style={{...inputStyle, minHeight:70, resize:'vertical'}} value={form.descripcion||''} onChange={e => setForm(f => ({...f, descripcion:e.target.value}))} placeholder="De que tratara el foro..." />
            <label style={labelStyle}>Tema / Categoria</label>
            <input style={inputStyle} value={form.tema||''} onChange={e => setForm(f => ({...f, tema:e.target.value}))} placeholder="Ej: Medio ambiente y tecnologia" />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={labelStyle}>Nivel</label>
                <select style={inputStyle} value={form.nivel_id||''} onChange={e => setForm(f => ({...f, nivel_id:e.target.value}))}>
                  <option value=''>Seleccionar nivel</option>
                  {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Moderador</label>
                <select style={inputStyle} value={form.profesor_id||''} onChange={e => setForm(f => ({...f, profesor_id:e.target.value}))}>
                  <option value=''>Seleccionar moderador</option>
                  {profesores.map(p => <option key={p.id} value={p.id}>{p.nombre} {p.apellido}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Fecha</label>
                <input type='date' style={inputStyle} value={form.fecha||''} onChange={e => setForm(f => ({...f, fecha:e.target.value}))} />
              </div>
              <div>
                <label style={labelStyle}>Cupos (max. 10)</label>
                <input type='number' min={1} max={10} style={inputStyle} value={form.cupos_total||10} onChange={e => setForm(f => ({...f, cupos_total:parseInt(e.target.value)}))} />
              </div>
              <div>
                <label style={labelStyle}>Hora inicio</label>
                <input type='time' style={inputStyle} value={form.hora_inicio||''} onChange={e => setForm(f => ({...f, hora_inicio:e.target.value}))} />
              </div>
              <div>
                <label style={labelStyle}>Hora fin</label>
                <input type='time' style={inputStyle} value={form.hora_fin||''} onChange={e => setForm(f => ({...f, hora_fin:e.target.value}))} />
              </div>
            </div>
            <label style={labelStyle}>Link del foro</label>
            <input style={inputStyle} value={form.link_meet||''} onChange={e => setForm(f => ({...f, link_meet:e.target.value}))} placeholder="https://meet.google.com/..." />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <button onClick={() => { setModal(null); setForm({}) }}
                style={{ padding:'10px 20px', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardarForo} disabled={guardando}
                style={{ padding:'10px 24px', background:'#7C3AED', color:'#fff', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                {guardando ? 'Guardando...' : form.id ? 'Actualizar' : 'Crear foro'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal lectura */}
      {modal === 'lectura' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:640, width:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h2 style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>{form.id ? 'Editar lectura' : 'Nueva lectura'}</h2>

            <label style={labelStyle}>Titulo</label>
            <input style={inputStyle} value={form.titulo||''} onChange={e => setForm(f => ({...f, titulo:e.target.value}))} placeholder="Ej: El cafetero y la luna" />

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
              <div>
                <label style={labelStyle}>Tipo</label>
                <select style={inputStyle} value={form.tipo||'cuento'} onChange={e => setForm(f => ({...f, tipo:e.target.value}))}>
                  <option value='cuento'>📖 Cuento corto</option>
                  <option value='noticia'>📰 Noticia</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Nivel</label>
                <select style={inputStyle} value={form.nivel||'A1'} onChange={e => setForm(f => ({...f, nivel:e.target.value}))}>
                  {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Min. de lectura</label>
                <input type='number' min={1} style={inputStyle} value={form.tiempo_lectura_min||3} onChange={e => setForm(f => ({...f, tiempo_lectura_min:parseInt(e.target.value)}))} />
              </div>
            </div>

            <label style={labelStyle}>Resumen corto (aparece en la tarjeta, opcional)</label>
            <input style={inputStyle} value={form.resumen||''} onChange={e => setForm(f => ({...f, resumen:e.target.value}))} placeholder="Una frase que resuma la lectura..." />

            <label style={labelStyle}>Texto completo</label>
            <textarea style={{...inputStyle, minHeight:220, resize:'vertical', fontFamily:'inherit', lineHeight:1.6}}
              value={form.texto||''} onChange={e => setForm(f => ({...f, texto:e.target.value}))}
              placeholder="Pega o escribe aqui el cuento o la noticia completa..." />

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <button onClick={() => { setModal(null); setForm({}) }}
                style={{ padding:'10px 20px', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardarLectura} disabled={guardando}
                style={{ padding:'10px 24px', background:'#F59E0B', color:'#fff', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                {guardando ? 'Guardando...' : form.id ? 'Actualizar' : 'Publicar lectura'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal profesor */}
      {modal === 'profesor' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, maxWidth:480, width:'100%' }}>
            <h2 style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>{form.id ? 'Editar profesor' : 'Nuevo profesor'}</h2>

            <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
              <div style={{ width:56, height:56, borderRadius:'50%', background:'#F4F6FA', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, overflow:'hidden', flexShrink:0 }}>
                {form.foto_url ? <img src={form.foto_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '👤'}
              </div>
              <div>
                <label style={{ display:'inline-block', padding:'7px 14px', borderRadius:8, border:'1.5px solid #E2E6F0', background:'#fff', color:'#374151', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  {subiendoFoto ? 'Subiendo...' : (form.foto_url ? '📷 Cambiar foto' : '📷 Subir foto')}
                  <input type="file" accept="image/*" onChange={elegirFotoProfesor} disabled={subiendoFoto} style={{ display:'none' }} />
                </label>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={labelStyle}>Nombre</label>
                <input style={inputStyle} value={form.nombre||''} onChange={e => setForm(f => ({...f, nombre:e.target.value}))} placeholder="Nombre" />
              </div>
              <div>
                <label style={labelStyle}>Apellido</label>
                <input style={inputStyle} value={form.apellido||''} onChange={e => setForm(f => ({...f, apellido:e.target.value}))} placeholder="Apellido" />
              </div>
            </div>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} value={form.email||''} onChange={e => setForm(f => ({...f, email:e.target.value}))} placeholder="email@ejemplo.com" />
            <label style={labelStyle}>Biografia (opcional, la ve el estudiante)</label>
            <textarea style={{...inputStyle, minHeight:70, resize:'vertical'}} value={form.bio||''} onChange={e => setForm(f => ({...f, bio:e.target.value}))} placeholder="Breve descripcion del profesor..." />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <button onClick={() => { setModal(null); setForm({}) }}
                style={{ padding:'10px 20px', background:'#F4F6FA', color:'#374151', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardarProfesor} disabled={guardando}
                style={{ padding:'10px 24px', background:'#059669', color:'#fff', border:'none', borderRadius:10, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                {guardando ? 'Guardando...' : form.id ? 'Actualizar' : 'Crear profesor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {verAsistenciaForo && (
        <ModalAsistenciaForo foro={verAsistenciaForo} usuario={usuario} mostrarMensaje={mostrarMensaje} onCerrar={() => setVerAsistenciaForo(null)} />
      )}
    </div>
  )
}
